import { createFileRoute } from '@tanstack/react-router';
import { Editor } from '@tiptap/react';
import { useCallback, useRef } from 'react';

import { useBesluitId, usePatchBesluitId } from '@/api/queries/besluit';
import { useOpenModal } from '@/components/Modal';
import { RegularImage } from '@/icons/components';
import { EditorAddImage } from '@/modals/AddImage';
import { SaveFailed } from '@/modals/SaveFailed';
import { ErrorMessage } from '@/utils/errorMessage';

import { ContentTemplate } from './-components/ContentTemplate/ContentTemplate';
import { AUTOSAVE_MUTATION_KEY } from './-components/Navigation/Navigation';
import { ToolbarContentPage } from './-components/ToolbarContentPage/ToolbarContentPage';

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/bijlage'
)({
  loader: ({ params: { besluitId } }) =>
    useBesluitId.ensureQueryData(besluitId),
  component: function Bijlage() {
    const { besluitId } = Route.useParams();

    const openModal = useOpenModal();

    const textEditorRef = useRef<Editor>(null);

    const besluit = useBesluitId(besluitId);
    const patchBesluit = usePatchBesluitId({
      mutationKey: AUTOSAVE_MUTATION_KEY,
    });

    const onSave = useCallback(
      (value: string) => {
        if (besluit.data?.Bijlage === value) return;

        patchBesluit.mutate(
          {
            id: besluitId,
            data: {
              Bijlage: value,
            },
          },
          {
            onError: (error) => {
              const errorMessage = ErrorMessage.getLocalErrorMessage(error);

              if (errorMessage) {
                openModal(SaveFailed, {
                  tryAgain: () =>
                    patchBesluit.mutateAsync({
                      id: besluitId,
                      data: {
                        Bijlage: value,
                      },
                    }),
                });
              }
            },
          }
        );
      },
      [patchBesluit, besluitId, openModal, besluit.data?.Bijlage]
    );

    if (!besluit.data) {
      return null;
    }

    return (
      <>
        <ContentTemplate
          id="page-bijlage-editor"
          title="Bijlage"
          defaultValue={besluit.data.Bijlage ?? ''}
          onSave={onSave}
          editorRef={textEditorRef}
          customOptions={[
            {
              icon: RegularImage,
              testId: 'texteditor-add-image',
              ariaLabel: 'Afbeelding toevoegen',
              onClick: () => {
                const initialSelection =
                  textEditorRef.current?.isActive('tableCell') ||
                  textEditorRef.current?.isActive('paragraph')
                    ? textEditorRef.current?.view.state.selection.$anchor.pos
                    : null;
                openModal(EditorAddImage, {
                  onAddImage: (base64Image, alt) => {
                    textEditorRef.current
                      ?.chain()
                      .focus(initialSelection)
                      .setImage({ src: base64Image, alt })
                      .run();
                  },
                });
              },
            },
          ]}
        />

        <ToolbarContentPage besluit={besluit.data} />
      </>
    );
  },
});
